import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useBankdetailsdraftFetchMutation,
  useUpdateBankMutation,
  useDeleteBankDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { CommonToast } from "../../../common/Toast";
import { liveSectionRows, mergeSectionDrafts, sectionRecordOf } from "./sectionDrafts";
import { BANK_SPEC } from "./bankingFields";

/* Banking Information's data.
 *
 * TRANSCRIBED from BankingInformation.jsx's effect and its two delete
 * branches. The draft merge is the flow-wide one.
 */
export const useBankingSection = ({ onStatusChange } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [draftRows, setDraftRows] = useState([]);

  const [fetchDrafts] = useBankdetailsdraftFetchMutation();
  const [updateBank] = useUpdateBankMutation();
  const [deleteBankDraft] = useDeleteBankDraftMutation();

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  const loadDrafts = useCallback(async () => {
    if (!companyID) return;
    try {
      const res = await fetchDrafts({ companyID, token });
      /* Transcribed: the rows live under the nested `data.data`. */
      setDraftRows(
        mergeSectionDrafts(res?.data?.data || [], currentEditingCompany, BANK_SPEC)
      );
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. Falls back to what is already on record. */
      setDraftRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token, currentEditingCompany]);

  useEffect(() => {
    if (!currentEditingCompany) return;
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany]);

  const usingDrafts = draftRows.length > 0;
  const rows = usingDrafts
    ? draftRows
    : liveSectionRows(currentEditingCompany, BANK_SPEC).map((row) =>
        sectionRecordOf(row, BANK_SPEC)
      );

  const refresh = () => {
    /* A bare refetch() promise, rejected, surfaces as an unhandled rejection
       the dev overlay reports as "[object Object]". */
    Promise.resolve(refetch()).catch(() => {});
    loadDrafts();
  };

  /* Transcribed: a draft row is deleted outright and a null body counts as success; a live row is SOFT-deleted with is_deleted: 1, sent as FormData exactly as... */
  const remove = async (record) => {
    try {
      if (record?.[BANK_SPEC.draftIdKey]) {
        const res = await deleteBankDraft({ data: record[BANK_SPEC.draftIdKey] });
        if (res?.data == null) {
          CommonToast.success(BANK_SPEC.deleteMessage);
          refresh();
        }
        return;
      }

      if (record?.[BANK_SPEC.idKey]) {
        const formData = new FormData();
        formData.append("is_deleted", 1);
        const res = await updateBank({
          data: formData,
          bankId: record[BANK_SPEC.idKey],
          token,
        }).unwrap();

        if (Number(res?.is_deleted) === 1) {
          onStatusChange?.();
          CommonToast.success(BANK_SPEC.deleteMessage);
          refresh();
        }
      }
    } catch (error) {
      /* The live handler reports with alert() and has no catch on the
         unwrapped call. DRF's refusal arrives as `data.detail`. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't delete this bank account."
      );
    }
  };

  return { rows, usingDrafts, remove, refresh };
};

export default useBankingSection;
